import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ProjectCard } from "@/components/ProjectCard";
import { FilterSidebar } from "@/components/FilterSidebar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Filter, Briefcase, Users, IndianRupee, Sparkles, Loader2, ChevronDown } from "lucide-react";

export default function HomePage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, total: 0, has_more: false });
  
  // Filters
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [costRange, setCostRange] = useState([0, 100]);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    fetchProjects(1, true); // Reset to page 1 when filters change
    fetchStats();
  }, [search, category, costRange]);

  const fetchProjects = async (page = 1, reset = false) => {
    if (reset) {
      setLoading(true);
      setProjects([]);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (category) params.append('category', category);
      if (costRange[0] > 0) params.append('min_cost', costRange[0] * 100000);
      if (costRange[1] < 100) params.append('max_cost', costRange[1] * 100000);
      params.append('page', page);
      params.append('limit', 20);
      
      const response = await axios.get(`${API}/projects?${params.toString()}`);
      
      // Handle both old (array) and new (object with pagination) response formats
      if (response.data.projects) {
        // New paginated response
        if (reset) {
          setProjects(response.data.projects);
        } else {
          setProjects(prev => [...prev, ...response.data.projects]);
        }
        setPagination(response.data.pagination);
      } else {
        // Old array response (fallback)
        setProjects(response.data);
        setPagination({ page: 1, total: response.data.length, has_more: false });
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
    setLoading(false);
    setLoadingMore(false);
  };

  const loadMore = () => {
    if (pagination.has_more && !loadingMore) {
      fetchProjects(pagination.page + 1, false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/projects/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const resetFilters = () => {
    setSearch('');
    setCategory('');
    setCostRange([0, 100]);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section - Light Theme */}
      <section className="relative overflow-hidden bg-gradient-to-br from-orange-50 via-white to-blue-50 border-b border-gray-200">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23F97316' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
        
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-20 relative">
          <div className="max-w-3xl">
            <h1 className="font-rajdhani font-bold text-4xl md:text-6xl text-gray-800 leading-tight mb-4">
              अपना <span className="text-orange-500">उद्योग</span> शुरू करें
            </h1>
            <p className="text-gray-600 text-lg md:text-xl mb-8 leading-relaxed">
              1500+ व्यापार आइडियाज़ की विस्तृत जानकारी – लागत, मशीनरी, मुनाफा, और सप्लायर सब एक जगह पर
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={() => document.getElementById('projects-section').scrollIntoView({ behavior: 'smooth' })}
                className="bg-orange-500 hover:bg-orange-600 text-white text-lg px-8 py-6 rounded-xl shadow-lg shadow-orange-200"
                data-testid="explore-btn"
              >
                प्रोजेक्ट्स देखें
              </Button>
              <Button
                variant="outline"
                className="border-gray-300 text-gray-700 hover:bg-gray-100 text-lg px-8 py-6 rounded-xl"
                data-testid="ai-btn"
              >
                <Sparkles className="w-5 h-5 mr-2 text-orange-500" />
                AI से पूछें
              </Button>
            </div>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-3 gap-4 mt-12 max-w-xl">
              <div className="bg-white/80 backdrop-blur border border-gray-200 rounded-xl p-4 shadow-sm">
                <Briefcase className="w-6 h-6 text-orange-500 mb-2" />
                <p className="font-rajdhani font-bold text-2xl text-gray-800">{stats.total}+</p>
                <p className="text-xs text-gray-500">प्रोजेक्ट्स</p>
              </div>
              <div className="bg-white/80 backdrop-blur border border-gray-200 rounded-xl p-4 shadow-sm">
                <Users className="w-6 h-6 text-emerald-500 mb-2" />
                <p className="font-rajdhani font-bold text-2xl text-gray-800">9+</p>
                <p className="text-xs text-gray-500">श्रेणियां</p>
              </div>
              <div className="bg-white/80 backdrop-blur border border-gray-200 rounded-xl p-4 shadow-sm">
                <IndianRupee className="w-6 h-6 text-blue-500 mb-2" />
                <p className="font-rajdhani font-bold text-2xl text-gray-800">₹499</p>
                <p className="text-xs text-gray-500">प्रति रिपोर्ट</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Main Content */}
      <section id="projects-section" className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-72 shrink-0">
            <FilterSidebar
              search={search}
              setSearch={setSearch}
              category={category}
              setCategory={setCategory}
              costRange={costRange}
              setCostRange={setCostRange}
              onReset={resetFilters}
            />
          </aside>

          {/* Mobile Filter Button */}
          <div className="lg:hidden">
            <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" className="w-full border-gray-300 text-gray-700 hover:bg-gray-100 rounded-xl">
                  <Filter className="w-4 h-4 mr-2" />
                  फ़िल्टर और खोज
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-white border-gray-200 w-80">
                <FilterSidebar
                  search={search}
                  setSearch={setSearch}
                  category={category}
                  setCategory={setCategory}
                  costRange={costRange}
                  setCostRange={setCostRange}
                  onReset={resetFilters}
                  isMobile
                  onClose={() => setFilterOpen(false)}
                />
              </SheetContent>
            </Sheet>
          </div>

          {/* Projects Grid */}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-rajdhani font-bold text-2xl text-gray-800">
                सभी प्रोजेक्ट्स
                <span className="text-gray-500 text-lg ml-2">
                  ({projects.length} / {pagination.total || stats?.total || 0})
                </span>
              </h2>
            </div>

            {loading ? (
              <div className="grid md:grid-cols-2 gap-4">
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} className="h-48 bg-gray-200 rounded-xl" />
                ))}
              </div>
            ) : projects.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-xl border border-gray-200">
                <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl text-gray-600">कोई प्रोजेक्ट नहीं मिला</h3>
                <p className="text-gray-500 mt-2">अपने फ़िल्टर बदलकर देखें</p>
              </div>
            ) : (
              <>
                <div className="grid md:grid-cols-2 gap-4" data-testid="projects-grid">
                  {projects.map((project, index) => (
                    <ProjectCard key={project.id} project={project} index={index} />
                  ))}
                </div>
                
                {/* Load More Button */}
                {pagination.has_more && (
                  <div className="flex justify-center mt-8">
                    <Button
                      onClick={loadMore}
                      disabled={loadingMore}
                      className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg font-bold rounded-xl shadow-lg shadow-orange-200"
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          लोड हो रहा है...
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-5 h-5 mr-2" />
                          और प्रोजेक्ट देखें ({pagination.total - projects.length} बाकी)
                        </>
                      )}
                    </Button>
                  </div>
                )}
                
                {/* Pagination Info */}
                {!pagination.has_more && projects.length > 0 && (
                  <p className="text-center text-gray-500 mt-8">
                    सभी {projects.length} प्रोजेक्ट दिखाए गए
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
